import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../components/ui/Button.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { useTranslation } from '../i18n/useTranslation.js';
import { updatePreferences } from '../services/authApi.js';

// The full catalog of home-dashboard widgets, in display order. Each key is
// stable and stored in the user's preferences; the labelKey reuses existing
// dashboard i18n keys.
export const DASHBOARD_WIDGETS = [
  { key: 'revenue', labelKey: 'dashboard.revenue' },
  { key: 'expenses', labelKey: 'dashboard.expenses' },
  { key: 'netProfit', labelKey: 'dashboard.netProfit' },
  { key: 'cash', labelKey: 'dashboard.cash' },
  { key: 'ar', labelKey: 'dashboard.ar' },
  { key: 'ap', labelKey: 'dashboard.ap' },
  { key: 'openSales', labelKey: 'dashboard.openSales' },
  { key: 'openPurchases', labelKey: 'dashboard.openPurchases' },
  { key: 'forecast', labelKey: 'dashboard.forecast' },
  { key: 'overdue', labelKey: 'dashboard.overdue' },
  { key: 'trendChart', labelKey: 'dashboard.chartTitle' },
  { key: 'pipelineChart', labelKey: 'dashboard.pipeline' },
  { key: 'agingChart', labelKey: 'finance.chart.aging' },
  { key: 'statusChart', labelKey: 'finance.chart.invoiceStatus' },
];

const ALL_KEYS = DASHBOARD_WIDGETS.map((w) => w.key);

export function DashboardConfigModal({ open, onClose, enabledKeys }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(() => new Set(enabledKeys ?? ALL_KEYS));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setSelected(new Set(enabledKeys ?? ALL_KEYS)); setError(null); }
  }, [open, enabledKeys]);

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const save = useMutation({
    mutationFn: () => updatePreferences({ dashboardWidgets: ALL_KEYS.filter((k) => selected.has(k)) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['preferences'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  return (
    <Modal open={open} onClose={onClose} title={t('dashboard.config.title')}
      footer={<>
        <Button variant="ghost" onClick={() => setSelected(new Set(ALL_KEYS))}>{t('dashboard.config.all')}</Button>
        <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button>
      </>}>
      <p className="muted text-sm">{t('dashboard.config.intro')}</p>
      <div className="user-admin-roles" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
        {DASHBOARD_WIDGETS.map((w) => (
          <label key={w.key} className="user-admin-role">
            <input type="checkbox" checked={selected.has(w.key)} onChange={() => toggle(w.key)} />
            <span>{t(w.labelKey)}</span>
          </label>
        ))}
      </div>
      {error && <div className="alert danger">{error}</div>}
    </Modal>
  );
}
